import { Toaster, toast, type ToasterProps } from "sonner";

/**
 * Snackbar — manifest §Snackbar. Thin wrapper over sonner.
 *
 *   // once, near the app root:
 *   <SnackbarProvider />
 *
 *   // anywhere:
 *   snackbar("Booking saved");
 *   snackbar("Upload failed", { action: { label: "Retry", onClick: retry } });
 *
 * Styling note: sonner paints toasts from the `--normal-*` CSS variables on
 * the toaster via a stylesheet it injects at runtime (after Tailwind's), so
 * toast classes cannot reliably win the cascade. The provider instead
 * overrides those variables with semantic token variables — surface-inverse /
 * on-inverse (mode-locked) — which flip automatically with [data-theme],
 * and sonner's default 8px toast radius already matches the rounded-lg
 * scale. No `theme` prop wiring is needed.
 */

const snackbarStyleDefaults = {
  style: {
    "--normal-bg": "var(--color-surface-inverse)",
    "--normal-text": "var(--color-on-inverse)",
    "--normal-border": "var(--color-surface-inverse)",
  } as React.CSSProperties,
};

export interface SnackbarProviderProps extends Omit<ToasterProps, "children"> {}

/**
 * Mount ONCE, self-closing, near the app root — it must NOT wrap children
 * (sonner's Toaster silently unmounts any subtree passed as children):
 *
 *   <App />
 *   <SnackbarProvider />
 */
export function SnackbarProvider(props: SnackbarProviderProps) {
  return <Toaster position="bottom-center" {...snackbarStyleDefaults} {...props} />;
}
SnackbarProvider.displayName = "SnackbarProvider";

export interface SnackbarOptions {
  /** Secondary line under the message. */
  description?: React.ReactNode;
  /** Auto-dismiss delay in ms; pass Infinity to keep it open. */
  duration?: number;
  /** Trailing action button. */
  action?: { label: React.ReactNode; onClick: () => void };
  /** Called when the snackbar is dismissed (swipe, close or auto-dismiss). */
  onDismiss?: () => void;
}

/** Fire a snackbar. Mirrors sonner's `toast(message, data)` but with a tiny surface. */
export function snackbar(message: React.ReactNode, opts?: SnackbarOptions) {
  return toast(message, opts);
}
